/**
 * Lightweight CSS-in-JS system for Tronic SDK
 * Zero dependencies - Pure DOM manipulation
 */

export type CSSProperties = {
  [K in keyof CSSStyleDeclaration]?: CSSStyleDeclaration[K] | string | number;
};

export interface StyleObject {
  [selector: string]: CSSProperties | { [keyframeStep: string]: CSSProperties };
}

/**
 * Convert camelCase to kebab-case for CSS properties
 */
function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Convert CSS properties object to CSS string
 */
function propertiesToCSS(properties: CSSProperties): string {
  return Object.entries(properties)
    .map(([key, value]) => {
      const cssKey = camelToKebab(key);
      let cssValue = value as string;

      if (typeof value === 'number' && !['opacity', 'zIndex', 'order', 'flex'].includes(key)) {
        cssValue = `${value}px`;
      }

      return `  ${cssKey}: ${cssValue};`;
    })
    .join('\n');
}

/**
 * Convert a style object to CSS string
 */
export function styleObjectToCSS(styles: StyleObject): string {
  return Object.entries(styles)
    .map(([selector, properties]) => {
      if (selector.startsWith('@keyframes')) {
        return generateKeyframesCSS(selector, properties as { [keyframeStep: string]: CSSProperties });
      }
      const cssProperties = propertiesToCSS(properties as CSSProperties);
      return `${selector} {\n${cssProperties}\n}`;
    })
    .join('\n\n');
}

/**
 * Generate CSS for @keyframes rules
 */
function generateKeyframesCSS(selector: string, properties: { [keyframeStep: string]: CSSProperties }): string {
  const keyframeSteps = Object.entries(properties)
    .map(([step, stepProperties]) => {
      if (typeof stepProperties === 'object' && stepProperties !== null) {
        const stepCSS = propertiesToCSS(stepProperties);
        return `  ${step} {\n  ${stepCSS.split('\n').join('\n  ')}\n  }`;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');

  return `${selector} {\n${keyframeSteps}\n}`;
}

/**
 * Apply styles directly to an element using CSS properties object
 */
export function applyStylesToElement(element: HTMLElement, styles: CSSProperties): void {
  Object.entries(styles).forEach(([key, value]) => {
    const styleKey = key as keyof CSSStyleDeclaration;
    let styleValue = value as string;

    if (typeof value === 'number' && !['opacity', 'zIndex', 'order', 'flex'].includes(key)) {
      styleValue = `${value}px`;
    }

    // eslint-disable-next-line no-param-reassign
    (element.style as any)[styleKey] = styleValue;
  });
}

/**
 * Create and inject CSS styles into the document head
 */
export function injectCSS(css: string, id?: string): HTMLStyleElement {
  if (id) {
    const existing = document.getElementById(id);
    if (existing) {
      existing.remove();
    }
  }

  const style = document.createElement('style');
  if (id) {
    style.id = id;
  }
  style.textContent = css;

  document.head.appendChild(style);

  return style;
}

/**
 * Interface for iframe positioning configuration
 */
export interface IframePositioningConfig {
  position: 'top-right' | 'bottom-right' | 'bottom-left';
  isMobile: boolean;
  launcherWidth?: number;
  gap?: number;
  margin?: number;
}

/**
 * Calculate dynamic iframe styles based on current state and launcher position
 */
export function calculateIframeStyles(config: IframePositioningConfig): CSSProperties {
  const { position, isMobile, launcherWidth = 0, gap = 20, margin = 20 } = config;

  const baseStyles: CSSProperties = {
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, top, left, right, bottom',
  };

  if (isMobile) {
    return {
      ...baseStyles,
      width: 'calc(100% - 40px)',
      height: 'calc(100% - 40px)',
      top: '20px',
      right: '20px',
      bottom: '20px',
      left: '20px',
    };
  }

  // Desktop: iframe width at least 440px; mobile uses full width via isMobile branch above
  const normalStyles: CSSProperties = {
    ...baseStyles,
    height: '80svh',
    width: 'auto',
    aspectRatio: '9 / 19.5',
    minHeight: '300px',
    minWidth: '440px',
    maxWidth: '440px',
  };

  switch (position) {
    case 'top-right':
      return {
        ...normalStyles,
        top: '100px',
        right: '20px',
        bottom: '20px',
        left: 'auto',
      };
    case 'bottom-right':
      return {
        ...normalStyles,
        top: 'auto',
        right: '20px',
        bottom: '100px',
        left: 'auto',
      };
    case 'bottom-left':
      return {
        ...normalStyles,
        top: 'auto',
        right: 'auto',
        bottom: '100px',
        left: '20px',
      };
    default:
      return {
        ...normalStyles,
        top: 'auto',
        right: `calc(${margin}px + ${launcherWidth}px + ${gap}px)`,
        bottom: '20px',
        left: 'auto',
      };
  }
}

/**
 * Apply transform origin based on launcher position for contextual animations
 */
export function calculateTransformOrigin(position: string): CSSProperties {
  switch (position) {
    case 'top-right':
      return { transformOrigin: 'top right' };
    case 'bottom-right':
      return { transformOrigin: 'bottom right' };
    case 'bottom-left':
      return { transformOrigin: 'bottom left' };
    default:
      return { transformOrigin: 'center' };
  }
}

/**
 * Apply complete iframe positioning with all necessary styles
 */
export function applyIframePositioning(iframeContainer: HTMLElement, config: IframePositioningConfig): void {
  // Clear any existing position styles
  const style = iframeContainer.style;
  style.removeProperty('top');
  style.removeProperty('right');
  style.removeProperty('bottom');
  style.removeProperty('left');
  style.removeProperty('width');
  style.removeProperty('height');

  // Apply transform origin for smooth animations
  const transformOrigin = calculateTransformOrigin(config.position);
  applyStylesToElement(iframeContainer, transformOrigin);

  // Apply dynamic positioning styles
  const positioningStyles = calculateIframeStyles(config);
  applyStylesToElement(iframeContainer, positioningStyles);
}

/**
 * Predefined style configurations for the iframe
 */
export const IFRAME_STYLES = {
  // Container base styles
  containerBase: {
    position: 'fixed',
    zIndex: 2147483646, // High z-index but below launcher (2147483647)
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // This clips the iframe content to rounded corners
    borderRadius: '16px',
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, transform, opacity',
    // Anti-bleeding techniques
    border: '1px solid rgba(0, 0, 0, 0.1)',
    isolation: 'isolate', // Create new stacking context
    contain: 'layout style paint', // Optimize containment
    // Fallback clip-path for stubborn bleeding
    clipPath: 'inset(0 round 16px)',
  } as CSSProperties,

  // Container states
  containerHidden: {
    opacity: 0,
    transform: 'scale(0)',
    pointerEvents: 'none',
  } as CSSProperties,

  containerVisible: {
    opacity: 1,
    transform: 'scale(1)',
    pointerEvents: 'auto',
  } as CSSProperties,

  // Animation origins based on launcher position for ultra-smooth animations
  animationOriginTopRight: {
    transformOrigin: 'top right',
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, transform',
  } as CSSProperties,

  animationOriginBottomRight: {
    transformOrigin: 'bottom right',
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, transform',
  } as CSSProperties,

  animationOriginBottomLeft: {
    transformOrigin: 'bottom left',
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, transform',
  } as CSSProperties,

  animationOriginCenter: {
    transformOrigin: 'center',
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
    willChange: 'width, height, transform',
  } as CSSProperties,

  containerNormal: {
    height: '80svh',
    width: 'auto',
    aspectRatio: '9 / 19.5',
    minHeight: '300px',
    minWidth: '440px',
    maxWidth: '440px',
    bottom: '20px',
    right: '20px',
  } as CSSProperties,

  // Mobile specific styles - Full screen with 20px margin
  containerMobile: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    top: '20px',
    right: '20px',
    bottom: '20px',
    left: '20px',
  } as CSSProperties,

  containerMobileTopRight: {
    width: 'calc(100% - 40px)',
    height: 'calc(100svh - 120px)',
    top: '100px',
    right: '20px',
    bottom: '20px',
    left: '20px',
  } as CSSProperties,

  containerMobileBottomRight: {
    width: 'calc(100% - 40px)',
    height: 'calc(100svh - 120px)',
    top: '20px',
    right: '20px',
    bottom: '100px',
    left: '20px',
  } as CSSProperties,

  containerMobileBottomLeft: {
    width: 'calc(100% - 40px)',
    height: 'calc(100svh - 120px)',
    top: '20px',
    right: '20px',
    bottom: '100px',
    left: '20px',
  } as CSSProperties,

  // Iframe styles optimized for smooth animations
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    zIndex: 2147483648,
    borderRadius: '15px', // Slightly smaller than container to prevent bleeding
    backgroundColor: 'transparent', // Remove white background that causes bleeding
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.03)',
    // Ensure iframe content respects container clipping
    overflow: 'hidden',
    // Additional anti-bleeding properties
    clipPath: 'inset(0 round 15px)', // Enforce rounded clipping on iframe itself
    backfaceVisibility: 'hidden', // Prevent rendering artifacts
    transform: 'translateZ(0)', // Force hardware acceleration and create layer
    // Smooth rendering optimizations
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    willChange: 'transform', // Optimize for transforms
    // Transition for iframe content smoothness
    transition: 'all 0.7s cubic-bezier(0.23, 1, 0.32, 1)',
  } as CSSProperties,
} as const;
